import React, { useMemo, useRef, useState } from "react";
import classnames from "classnames";
import { components as ReactSelectComponents, OptionProps } from "react-select";

import CompactSelect from "@vahak/core-ui/dist/components/CompactSelect";
import Checkbox from "@vahak/core-ui/dist/components/Checkbox";
import { SelectOption } from "@vahak/core-ui/dist/components/Select";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";

import FieldShellStyles from "../MarketplaceLoadFilterCard.module.scss";
import {
    createQuoteFormLocationControl,
    createQuoteFormLocationInput,
    createQuoteFormLocationMenuAlways,
    createQuoteFormLocationPlaceholder,
    createQuoteFormMultiValueContainer,
    filterMarketplaceStateOption,
    hiddenMultiValue
} from "./marketplaceQuoteFormFieldParts";
import {
    getMultiSelectDisplayLabel,
    isAnyDestinationSelected,
    MARKETPLACE_ANY_DESTINATION_ID,
    MARKETPLACE_DESTINATION_STATE_OPTIONS,
    resolveMultiDestinationChange
} from "./marketplaceFilterStateOptions";
import MarketplaceFilterMobileSheet from "./MarketplaceFilterMobileSheet";

interface MarketplaceFilterStateMultiFieldProps {
    id: string;
    placeholder: string;
    icon: React.ReactNode;
    value?: SelectOption<number>[];
    mobileTitle: string;
    onChange: (value?: SelectOption<number>[]) => void;
}

const MarketplaceFilterStateMultiField = ({
    id,
    placeholder,
    icon,
    value = [],
    mobileTitle,
    onChange
}: MarketplaceFilterStateMultiFieldProps) => {
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const [mobileOpen, setMobileOpen] = useState(false);
    const selectRef = useRef<any>(null);

    const selectedValue = value.filter((item) => item.id !== MARKETPLACE_ANY_DESTINATION_ID);
    const displayLabel = getMultiSelectDisplayLabel(value);

    const handleChange = (selected: SelectOption<number>[] | null) => {
        const selectedArr = Array.isArray(selected) ? selected : selected ? [selected] : [];
        onChange(resolveMultiDestinationChange(value, selectedArr));
    };

    const selectComponents = useMemo(
        () => ({
            DropdownIndicator: () => null,
            Control: createQuoteFormLocationControl(FieldShellStyles, icon),
            Input: createQuoteFormLocationInput(),
            Placeholder: createQuoteFormLocationPlaceholder(FieldShellStyles),
            Menu: createQuoteFormLocationMenuAlways(FieldShellStyles),
            Option: (props: OptionProps<SelectOption<number>, true>) => {
                const currentValue = [...(props.getValue() ?? [])];
                const isAnyOption = props.data.id === MARKETPLACE_ANY_DESTINATION_ID;
                const isChecked = isAnyOption ? isAnyDestinationSelected(currentValue) : props.isSelected;

                return (
                    <ReactSelectComponents.Option
                        {...props}
                        isSelected={isChecked}
                        className={classnames(
                            FieldShellStyles.locationOption,
                            props.isFocused && FieldShellStyles.locationOptionActive
                        )}
                    >
                        <div className={FieldShellStyles.checkboxOptionRow}>
                            <span className={FieldShellStyles.locationOptionText}>{props.label}</span>
                            <Checkbox
                                id={`marketplace-filter-dest-${props.data.id}`}
                                name={`marketplace-filter-dest-${props.data.id}`}
                                label=""
                                value={isChecked}
                                onChange={() => null}
                            />
                        </div>
                    </ReactSelectComponents.Option>
                );
            },
            ValueContainer: createQuoteFormMultiValueContainer(FieldShellStyles, displayLabel),
            MultiValue: hiddenMultiValue
        }),
        [displayLabel, icon]
    );

    return (
        <div
            className={classnames(
                FieldShellStyles.fieldWrap,
                FieldShellStyles.fieldWrapSelect,
                FieldShellStyles.fieldWrapMulti,
                FieldShellStyles.fieldWrapSearchable
            )}
        >
            <CompactSelect
                ref={selectRef}
                id={id}
                name={id}
                isMulti
                closeMenuOnSelect
                hideSelectedOptions={false}
                option={MARKETPLACE_DESTINATION_STATE_OPTIONS}
                value={selectedValue}
                placeholder={placeholder}
                isClearable={!isMobileScreen && selectedValue.length > 0}
                isSearchable
                openMenuOnClick
                openMenuOnFocus
                filterOption={filterMarketplaceStateOption}
                menuPosition="absolute"
                onChange={handleChange as any}
                components={selectComponents as any}
                styles={{ multiValue: () => ({ display: "none" }) }}
                {...(isMobileScreen
                    ? {
                          menuIsOpen: false,
                          onMenuOpen: () => {
                              selectRef.current?.blur?.();
                              setMobileOpen(true);
                          }
                      }
                    : {})}
            />

            {mobileOpen ? (
                <MarketplaceFilterMobileSheet
                    title={mobileTitle}
                    options={MARKETPLACE_DESTINATION_STATE_OPTIONS}
                    value={value}
                    isMulti
                    onChange={(next) => onChange(next)}
                    onClose={() => setMobileOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default MarketplaceFilterStateMultiField;
